import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  useFontStyleForGrapheme,
  useGraphemeDisplayText,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { View } from "@-/com.npmjs.react-native";
import { hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";

function MorphemeInfoPanelDictionaryDecompositionItem({
  compact,
  component,
}: {
  compact: boolean;
  component: ReferenceGraphemeMorphemeSlice["components"][number];
}) {
  const fontStyle = useFontStyleForGrapheme(component.grapheme);
  // Render the display text, not the authored string: a variation sequence must be rewritten to
  // its private-use code point or it draws the plain base stroke (see `graphemeDisplayText`).
  const displayText = useGraphemeDisplayText(component.grapheme);
  const studyPinyinList = usePrevalentPinyin(component.grapheme, component.pinyinList);
  const pinyin = toDiacriticPinyin(studyPinyinList[0] ?? "-");

  return (
    <View
      className={
        compact ? "flex-row items-center gap-x-1" : "min-w-18.5 flex-1 justify-center px-2 py-1"
      }
    >
      <Text
        className={
          compact
            ? "text-center text-xl leading-6 text-foreground"
            : "text-center text-[24px] leading-7 text-foreground"
        }
        style={fontStyle}
        variant="glyph"
      >
        {displayText}
      </Text>
      <Text
        className={
          compact
            ? "text-[11px] text-muted-foreground"
            : "mt-1 text-center text-[12px] text-muted-foreground"
        }
      >
        {pinyin}
      </Text>
    </View>
  );
}

export function MorphemeInfoPanelDictionaryCompositionSection({
  compact = false,
  visibleComponentList,
}: {
  compact?: boolean;
  visibleComponentList: ReferenceGraphemeMorphemeSlice["components"];
}) {
  const title = hasMinLength(visibleComponentList, 2) ? "Composite Character" : "Simple Character";

  return (
    <View className={compact ? "min-w-0" : "min-h-0 flex-1"}>
      {compact && isNonEmpty(visibleComponentList) ? null : (
        <Text
          className={
            compact
              ? "text-xs text-muted-foreground"
              : "text-sm leading-5 font-semibold text-foreground"
          }
        >
          {title}
        </Text>
      )}
      {isNonEmpty(visibleComponentList) ? (
        <View
          className={
            compact
              ? "flex-row flex-wrap items-center gap-x-3 gap-y-1"
              : "mt-2 min-h-0 flex-1 flex-row flex-wrap items-center gap-2"
          }
        >
          {visibleComponentList.map((component, index) => (
            <MorphemeInfoPanelDictionaryDecompositionItem
              compact={compact}
              component={component}
              key={`${component.grapheme}-${index}`}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}
